import { jsonStringify } from 'utils/json';

import {
  BLOB_CACHE_MAX_SIZE_BYTES,
  BLOB_CACHE_NAME,
  CACHE_HEADERS,
  CACHE_KEY_PREFIX,
} from './BlobCacheService.constants';

class BlobCacheService {
  #cacheName;
  #maxSize;
  #keyPrefix;
  #isAvailable;

  constructor(
    cacheName = BLOB_CACHE_NAME,
    maxSize = BLOB_CACHE_MAX_SIZE_BYTES,
    keyPrefix = CACHE_KEY_PREFIX,
  ) {
    this.#cacheName = cacheName;
    this.#maxSize = maxSize;
    this.#keyPrefix = keyPrefix;
    this.#isAvailable = 'caches' in globalThis;
  }

  async get(blobStorageId) {
    if (!this.#isAvailable) {
      return null;
    }

    const cache = await caches.open(this.#cacheName);
    const cacheKey = `${this.#keyPrefix}${blobStorageId}`;
    const response = await cache.match(cacheKey);

    if (!response) {
      return null;
    }

    try {
      return await response.json();
    } catch {
      await cache.delete(cacheKey);

      return null;
    }
  }

  async set(blobStorageId, data) {
    if (!this.#isAvailable) {
      return;
    }

    const cache = await caches.open(this.#cacheName);
    const serialized = jsonStringify(data);
    const { size } = new Blob([serialized]);

    if (size > this.#maxSize) {
      return;
    }

    await this.#ensureCapacity(cache, size);

    const cacheKey = `${this.#keyPrefix}${blobStorageId}`;
    const response = new Response(serialized, {
      headers: {
        [CACHE_HEADERS.CONTENT_TYPE]: 'application/json',
        [CACHE_HEADERS.BLOB_SIZE]: String(size),
        [CACHE_HEADERS.CACHED_AT]: new Date().toISOString(),
      },
    });

    await cache.put(cacheKey, response);
  }

  async has(blobStorageId) {
    if (!this.#isAvailable) {
      return false;
    }

    const cache = await caches.open(this.#cacheName);
    const cacheKey = `${this.#keyPrefix}${blobStorageId}`;
    const response = await cache.match(cacheKey);

    return !!response;
  }

  async clear() {
    if (!this.#isAvailable) {
      return;
    }

    await caches.delete(this.#cacheName);
  }

  async getStats() {
    if (!this.#isAvailable) {
      return { itemCount: 0, totalSize: 0, maxSize: this.#maxSize };
    }

    const cache = await caches.open(this.#cacheName);
    const keys = await cache.keys();
    const entries = await Promise.all(
      keys.map(async request => {
        const response = await cache.match(request);
        const size = parseInt(response.headers.get(CACHE_HEADERS.BLOB_SIZE) || '0', 10);

        return { url: request.url, size };
      }),
    );

    return {
      itemCount: entries.length,
      totalSize: entries.reduce((sum, entry) => sum + entry.size, 0),
      maxSize: this.#maxSize,
    };
  }

  async #ensureCapacity(cache, requiredSize) {
    const keys = await cache.keys();

    const entries = await Promise.all(
      keys.map(async request => {
        const response = await cache.match(request);
        const size = parseInt(response.headers.get(CACHE_HEADERS.BLOB_SIZE) || '0', 10);
        const cachedAt = response.headers.get(CACHE_HEADERS.CACHED_AT) || '';

        return { request, size, cachedAt };
      }),
    );

    entries.sort((a, b) => a.cachedAt.localeCompare(b.cachedAt));

    const currentSize = entries.reduce((sum, entry) => sum + entry.size, 0);
    const sizeToFree = currentSize + requiredSize - this.#maxSize;

    if (sizeToFree <= 0) {
      return;
    }

    const entriesToEvict = [];
    const freed = entries.reduce((acc, entry) => {
      if (acc >= sizeToFree) {
        return acc;
      }

      entriesToEvict.push(entry);

      return acc + entry.size;
    }, 0);

    if (freed > 0) {
      await Promise.all(entriesToEvict.map(entry => cache.delete(entry.request)));
    }
  }
}

export default BlobCacheService;
